'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { CartOption } from '../_components/AddToCartButton';

interface SelectedOptionWithQuantity {
  optionGroupId: number;
  optionItemId: number;
  extraPrice: number;
  quantity: number;
}

interface GiftOptionContextValue {
  /** 선택된 옵션 Map (optionGroupId -> option) */
  selectedOptions: Map<number, SelectedOptionWithQuantity>;
  /** 옵션 없는 상품의 수량 */
  noOptionQuantity: number;
  /** 옵션 선택/해제 */
  handleOptionSelect: (
    optionGroupId: number,
    optionItemId: number,
    extraPrice: number,
  ) => void;
  /** 수량 변경 */
  handleQuantityChange: (optionGroupId: number, quantity: number) => void;
  /** 옵션 삭제 */
  handleOptionDelete: (optionGroupId: number) => void;
  /** 옵션 없는 상품 수량 변경 */
  setNoOptionQuantity: (quantity: number) => void;
  /** 장바구니용 옵션 배열 반환 */
  getCartOptions: (
    optionGroups: {
      optionGroupId?: number;
      optionGroupName?: string;
      optionItemInfoList?: {
        optionItemId?: number;
        itemName?: string;
        extraPrice?: number;
      }[];
    }[],
  ) => CartOption[];
  /** 선택 초기화 */
  resetSelection: () => void;
}

const GiftOptionContext = createContext<GiftOptionContextValue | null>(null);

export const GiftOptionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedOptions, setSelectedOptions] = useState<
    Map<number, SelectedOptionWithQuantity>
  >(new Map());
  const [noOptionQuantity, setNoOptionQuantity] = useState(1);

  // 옵션 선택 시 (그룹당 하나만 선택, 같은 옵션 클릭 시 선택 해제)
  const handleOptionSelect = useCallback(
    (optionGroupId: number, optionItemId: number, extraPrice: number) => {
      setSelectedOptions((prev) => {
        const existing = prev.get(optionGroupId);
        const newMap = new Map(prev);

        // 같은 옵션이 이미 선택되어 있으면 선택 해제
        if (existing?.optionItemId === optionItemId) {
          newMap.delete(optionGroupId);

          return newMap;
        }

        // 그룹 내 다른 옵션 선택 시 교체 (수량은 1로 리셋)
        newMap.set(optionGroupId, {
          optionGroupId,
          optionItemId,
          extraPrice,
          quantity: 1,
        });

        return newMap;
      });
    },
    [],
  );

  // 수량 변경
  const handleQuantityChange = useCallback(
    (optionGroupId: number, quantity: number) => {
      setSelectedOptions((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(optionGroupId);
        if (existing) {
          newMap.set(optionGroupId, { ...existing, quantity });
        }

        return newMap;
      });
    },
    [],
  );

  // 옵션 삭제
  const handleOptionDelete = useCallback((optionGroupId: number) => {
    setSelectedOptions((prev) => {
      const newMap = new Map(prev);
      newMap.delete(optionGroupId);

      return newMap;
    });
  }, []);

  // 장바구니용 옵션 정보 변환
  const getCartOptions = useCallback(
    (
      optionGroups: {
        optionGroupId?: number;
        optionGroupName?: string;
        optionItemInfoList?: {
          optionItemId?: number;
          itemName?: string;
          extraPrice?: number;
        }[];
      }[],
    ): CartOption[] => {
      return Array.from(selectedOptions.values()).map((opt) => {
        const optionGroup = optionGroups.find(
          (g) => g.optionGroupId === opt.optionGroupId,
        );
        const optionItem = optionGroup?.optionItemInfoList?.find(
          (item) => item.optionItemId === opt.optionItemId,
        );

        return {
          optionGroupId: opt.optionGroupId,
          optionGroupName: optionGroup?.optionGroupName ?? '',
          optionItemId: opt.optionItemId,
          optionItemName: optionItem?.itemName ?? '',
          extraPrice: opt.extraPrice,
          quantity: opt.quantity,
        };
      });
    },
    [selectedOptions],
  );

  // 선택 초기화
  const resetSelection = useCallback(() => {
    setSelectedOptions(new Map());
    setNoOptionQuantity(1);
  }, []);

  const value = useMemo(
    () => ({
      selectedOptions,
      noOptionQuantity,
      handleOptionSelect,
      handleQuantityChange,
      handleOptionDelete,
      setNoOptionQuantity,
      getCartOptions,
      resetSelection,
    }),
    [
      selectedOptions,
      noOptionQuantity,
      handleOptionSelect,
      handleQuantityChange,
      handleOptionDelete,
      getCartOptions,
      resetSelection,
    ],
  );

  return (
    <GiftOptionContext.Provider value={value}>
      {children}
    </GiftOptionContext.Provider>
  );
};

export const useGiftOption = () => {
  const context = useContext(GiftOptionContext);
  if (!context) {
    throw new Error('useGiftOption must be used within GiftOptionProvider');
  }

  return context;
};
